import { AccountService } from './accountService';
import { UserService } from './userService';
import { Service, ServiceResponse } from './service';
import { PublicService } from './publicService';
import { MessagesService } from './messagesService';
import { NetworkService } from './networkService';
import { ForumsService } from './forumsService';
import { BlogsService } from './blogsService';
import { ActivityService } from './activityService';
import { MediaService } from './mediaService';
import { NotificationsService } from './notificationsService';
import { CommunityService } from './communityService';

export class Server {
  public static account: AccountService = new AccountService();
  public static user: UserService = new UserService();
  public static public: PublicService = new PublicService();
  public static messages: MessagesService = new MessagesService();
  public static forums: ForumsService = new ForumsService();
  public static blogs: BlogsService = new BlogsService();
  public static activity: ActivityService = new ActivityService();
  public static media: MediaService = new MediaService();
  public static network: NetworkService = new NetworkService();
  public static notifications: NotificationsService = new NotificationsService();
  public static community: CommunityService = new CommunityService();
  public static initialized: boolean = false;
  
  protected static signUpDetails: any = null;
  public static synchronizing: boolean = false;

  public static async init(community:string = null) {
    if(community)
      Service.setCommunity(community);

    let promises = [];
    promises.push(Server.user.init());

    if(community) {
      promises.push(Server.messages.init());
      promises.push(Server.notifications.init());
      promises.push(Server.activity.init());
      promises.push(Server.forums.init());
    }

    let responses = await Promise.all(promises);

    for(let i = 0; i < responses.length; i++)
      if(!responses[i].success)
        return responses[i];

    Server.initialized = true;
  }

  public static getEnvironment() {
    return Service.getEnvironment();
  }

  public static async startSignUp(email: string, password: string): Promise<ServiceResponse> {
    let response = await Server.account.signUp(email, password);
    this.signUpDetails = response.success ? {email, password} : null;
    return response;
  }

  public static async completeSignUp(code: string): Promise<ServiceResponse> {
    let response = await Server.account.confirmRegistration(code);
    if(response.success)
      await this.login(this.signUpDetails.email, this.signUpDetails.password);
    return response;
  }

  public static async login(email: string, password: string): Promise<ServiceResponse> {
    let response = await Server.account.authenticate(email, password);
    if(!response.success)
      return response;

    let refreshToken = Server.account.getRefreshToken();
    window.localStorage.setItem('RefreshToken', refreshToken);
    window.localStorage.setItem('RefreshEmail', email);

    return Server.finishLogin();
  }

  public static hasAutoLogin(): boolean {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');
    return (email != null && token != null);
  }

  public static async autoLogin(): Promise<ServiceResponse> {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');

    if(!email || !token)
      return {success: false};

    let response = await Server.account.authenticateWithToken(email, token);
    if(!response.success)
      return response;

    return Server.finishLogin();
  }

  protected static async finishLogin() {
    Service.setAuthorizationToken(Server.account.getAuthorizationToken());

    let response = await Server.sync();
    if(!response.success)
      return response;

    let community = Service.getCommunity();
    if(community != '') {
      Server.network.setCommunity(community);
      await Server.network.connect();
    }
  
    // await this.user.setPresence('site');
    Server.account.notifyListeners('login');

    return {success: true};
  }

  public static async signOut() {
    await Server.account.signOut();
    Server.user.clear();
    Server.messages.clear();
    Server.network.disconnect();
    window.localStorage.removeItem('RefreshToken');
    window.localStorage.removeItem('RefreshEmail');
    Server.account.notifyListeners('logout');
    Service.setAuthorizationToken('');
  }

  public static async sync(): Promise<ServiceResponse> {
    Server.synchronizing = true;

    let response = await Server.user.sync();  
    if(!response.success) {
      Server.synchronizing = false;
      return response;
    }

    // must do after user is done
    await Server.messages.sync();  

    Server.synchronizing = false;

    return {success: true};
  }

  public static async refreshServiceLogin(): Promise<ServiceResponse> {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');

    if(email && token) {
      let response = await Server.account.authenticateWithToken(email, token);
      if(!response.success)
        return response;

      console.log('Authentication token refreshed.');
        
      Service.setAuthorizationToken(Server.account.getAuthorizationToken());

      return response;
    }

    return {success: false};
  }

  public static getServiceState() {
    let state:any = {
      notifications: 0,
      messages: 0,
      friends: 0,
      forums: 0,
      blogs: 0,
      activity: 0
    }

    if(!Server.account.isLoggedIn())
      return state;

    if(Service.getCommunity() == '')
      return state;

    // notifications
    let messages = Server.notifications.getMessages();
    let lastReadCookie = Server.user.getCookie('last-notification-read');
    let lastRead = lastReadCookie ? new Date(lastReadCookie) : null;
    
    for(let i = 0; i < messages.length; i++) {
      let msgDate = new Date(messages[i].date);
      let unread = msgDate > lastRead;
      if(unread)
        state.notifications++;
    }

    // friends
    let friends = Server.user.getFriends();
    for(let i = 0; i < friends.length; i++) 
      if(friends[i].state == 'invited')
        state.friends++;

    // messages
    let chats = Server.messages.getChats();
    for(let i = 0; i < chats.length; i++)
      if(chats[i].id != 'community')
        if(Server.messages.isNewChatMessages(chats[i].id))
          state.messages++;

    // forums
    state.forums = Server.forums.getUnreadForumCount();

    // activity
    state.activity = Server.activity.newPostsFromCache() ? 1 : 0;

    // blogs
    state.blogs = Server.blogs.getUnreadBlogCount();

    return state;
  }
}
